import { Button, Flex, P } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import { createPortal } from "react-dom";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";

import { Tab, TabBar } from "./editor.styled";
import { useDashboardData } from "./DashboardDataContext";
import DashboardDataProvider from "./DashboardDataProvider";
import DashboardEditPanel from "./DashboardEditPanel";
import DashboardRenderer from "./DashboardRenderer";

const TABS = [
  { key: "template", label: "Template" },
  { key: "css", label: "CSS" },
  { key: "queries", label: "Queries" },
];

const DashboardToolbar = () => {
  const { isEditing, toggleEdit, reset, activeTab, setActiveTab } =
    useDashboardData();
  const dashboardToolbar = document.getElementById("dashboard-toolbar");

  if (!dashboardToolbar) {
    return null;
  }

  return createPortal(
    <Flex justifyContent="flex-end" alignItems="center" gap="s" mr="r">
      {isEditing && (
        <>
          <TabBar>
            {TABS.map((tab) => (
              <Tab
                key={tab.key}
                type="button"
                $active={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabBar>
          <Button variant="ghost" small onClick={reset}>
            Reset
          </Button>
        </>
      )}
      <Button
        variant={isEditing ? "default" : "ghost"}
        small
        onClick={toggleEdit}
      >
        {isEditing ? "Done" : "Edit"}
      </Button>
    </Flex>,
    dashboardToolbar
  );
};

const DashboardContent = () => {
  const { enableAIDashboardEditor } = useWorkspaceFeatureFlags();
  const { isEditing } = useDashboardData();

  return (
    <Flex flexDirection="column" gap="r" height="100%">
      {enableAIDashboardEditor && <DashboardToolbar />}
      <Flex gap="r" alignItems="flex-start" height="100%">
        {isEditing && (
          <Flex
            flex="1"
            minWidth="0"
            maxWidth="30%"
            flexDirection="column"
            overflow="auto"
            height="100%"
          >
            <DashboardEditPanel />
          </Flex>
        )}
        <Flex
          flex="1"
          minWidth="0"
          flexDirection="column"
          height="100%"
          overflow={isEditing ? "auto" : undefined}
        >
          <DashboardRenderer />
        </Flex>
      </Flex>
    </Flex>
  );
};

const DataDrivenDashboardRenderer = ({ dashboard }) => {
  if (!dashboard.template) {
    return <P>Dashboard definition is missing a template.</P>;
  }

  return (
    <DashboardDataProvider dashboard={dashboard}>
      <DashboardContent />
    </DashboardDataProvider>
  );
};

DataDrivenDashboardRenderer.propTypes = {
  dashboard: PropTypes.shape({
    css: PropTypes.string.isRequired,
    queries: PropTypes.string.isRequired,
    template: PropTypes.string.isRequired,
  }).isRequired,
};

export default DataDrivenDashboardRenderer;
